/**
 * What the dashboard needs from a backend. `HttpBackend` talks to the Worker API; `DemoBackend` runs everything
 * in the browser on the memory adapter (the Artifact demo).
 */
import type { CommitInfo, FileStorage, PendingChanges, Storage } from '@hotpot/core';

export type Role = 'studio' | 'owner' | 'editor';

export interface Me {
  user: { id: string; email: string; name: string; studio: boolean };
  sites: SiteSummary[];
}

export interface SiteSummary {
  id: string;
  name: string;
  url: string;
  role: Role;
  repo?: string;
  branch?: string;
  /** Studio only: the branch drafts are saved to. */
  previewBranch?: string | null;
  previewUrl?: string;
  /** Saves are drafts until published. */
  drafts?: boolean;
}

export interface SiteInput {
  name: string;
  branch: string;
  url: string;
  /** Empty = no drafts: every save is published right away. */
  previewBranch: string;
  previewUrl: string;
}

/** Drafts on the preview branch (sites with a preview branch). */
export interface DraftsHandle {
  /** The preview site's address ('' in the demo). */
  previewUrl: string;
  pending(): Promise<PendingChanges>;
  publish(): Promise<CommitInfo | null>;
  discard(): Promise<void>;
}

export interface Member {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'editor';
  last_login_at: string | null;
  created_at: string;
}

export interface ActivityItem {
  action: string;
  detail: string;
  created_at: string;
  email: string | null;
  name: string | null;
}

export interface Operator {
  name: string;
  email: string;
}

export interface SiteHandle {
  site: SiteSummary;
  configText: string;
  storage: Storage;
  drafts?: DraftsHandle;
  /** Mark a file as open in the editor (or closed, with `leave`); returns the other people who have it open. */
  presence?: (path: string, leave?: boolean) => Promise<{ name: string; email: string }[]>;
  /** Upload a large file (a video) to the site's R2 bucket; returns its public URL. */
  uploadLarge: (storage: FileStorage, file: File, onProgress: (fraction: number) => void) => Promise<string>;
  /** Machine translation of a text (the CMS Worker's AI binding); rejects with code `translation_unavailable` when it is off. */
  translate: (text: string, from: string, to: string) => Promise<string>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public path?: string,
  ) {
    super(message);
  }
}

export interface Backend {
  demo: boolean;
  me(): Promise<Me | null>;
  requestLogin(email: string): Promise<void>;
  verifyToken(token: string): Promise<Me>;
  verifyCode(email: string, code: string): Promise<Me>;
  logout(): Promise<void>;
  updateName(name: string): Promise<Me>;
  openSite(id: string): Promise<SiteHandle>;
  addSite(input: SiteInput & { id: string; repo: string }): Promise<void>;
  updateSite(id: string, input: SiteInput): Promise<void>;
  members(siteId: string): Promise<Member[]>;
  invite(siteId: string, input: { email: string; name: string; role: 'owner' | 'editor' }): Promise<void>;
  setRole(siteId: string, userId: string, role: 'owner' | 'editor'): Promise<void>;
  removeMember(siteId: string, userId: string): Promise<void>;
  activity(siteId: string): Promise<ActivityItem[]>;
  legal(): Promise<Operator>;
  setupStatus(): Promise<{ github: { configured: boolean; installUrl: string | null } }>;
}
